import React, { useState, useEffect } from 'react';
import { useUser } from '../context/UserContext';
import { hasPrivilege } from '../utils/hasPrivilege';
import { useNavigate } from 'react-router-dom';
import { FiChevronDown, FiRefreshCw } from 'react-icons/fi';

const AcademyFeatures = () => {
  const { userData } = useUser();
  
  const [features, setFeatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const navigate = useNavigate();
  const apiUrl = process.env.REACT_APP_API_ADMIN_ACADEMY_FEATURE_LIST;

  const headers = {
    'x-authorization': process.env.REACT_APP_AUTH_TOKEN,
    'Content-Type': 'application/json',
  };

  const fetchFeatures = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(apiUrl, { headers });
      const result = await response.json();

      if (!result.success || !result.data) {
        throw new Error('Invalid response from server');
      }

      const academies = Array.isArray(result.data)
        ? result.data
        : Object.values(result.data);

      const mappedFeatures = academies.map(academy => ({
        id: academy.academyId,
        name: academy.academyName,
        type: (academy.attractions || []).map(a => a.name).join(', '),
        amenities: (academy.amenities || []).map(m => m.name).join(', '),
      }));

      setFeatures(mappedFeatures);
    } catch (err) {
      setError("Failed to load data from API.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFeatures();
  }, []);

  const header = [
    { key: 'name', label: 'Academy Name' },
    { key: 'type', label: 'Attraction' },
    { key: 'amenities', label: 'Amenities' },
  ];

  const handleEdit = (event, featureId) => {
    const url = `/newAcademyFeatures?featureId=${featureId}`;
    if (event.metaKey || event.ctrlKey) {
      window.open(url, "_blank");
    } else {
      navigate(url, { state: { featureId } });
    }
  };

  const handleCreate = (event) => {
    const url = `/newAcademyFeatures`;
    if (event.metaKey || event.ctrlKey) {
      window.open(url, "_blank");
    } else {
      navigate(url);
    }
  };

  const filteredFeatures = features.filter((f) => {
    const searchLower = searchTerm.toLowerCase();
    return (
      (f.name && f.name.toLowerCase().includes(searchLower)) ||
      (f.type && f.type.toLowerCase().includes(searchLower)) ||
      (f.access && f.access.toLowerCase().includes(searchLower))
    );
  });

  const handlePageClick = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const sortData = (data) => {
    const { key, direction } = sortConfig;
    if (!key) return data;

    return [...data].sort((a, b) => {
      let aValue = a[key] ?? '';
      let bValue = b[key] ?? '';

      if (typeof aValue === 'string') aValue = aValue.toLowerCase();
      if (typeof bValue === 'string') bValue = bValue.toLowerCase();

      const numA = parseFloat(aValue);
      const numB = parseFloat(bValue);
      const isNumeric = !isNaN(numA) && !isNaN(numB);

      if (isNumeric) {
        aValue = numA;
        bValue = numB;
      }

      if (aValue < bValue) return direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return direction === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const sortedData = sortData(filteredFeatures);
  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentRows = sortedData.slice(indexOfFirstRow, indexOfLastRow);
  const totalPages = Math.ceil(sortedData.length / rowsPerPage);
  const totalEntries = filteredFeatures.length;
  const indexOfFirstEntry = totalEntries > 0 ? (currentPage - 1) * rowsPerPage + 1 : 0;
  const indexOfLastEntry = totalEntries > 0 ? Math.min(currentPage * rowsPerPage, totalEntries) : 0;

  const handleNextPage = () => {
    if (currentPage < totalPages) setCurrentPage((prevPage) => prevPage + 1);
  };

  const handlePrevPage = () => {
    if (currentPage > 1) setCurrentPage((prevPage) => prevPage - 1);
  };

  const generatePageNumbers = () => {
    const pages = [];
    const totalPagesToShow = 5;

    if (totalPages <= totalPagesToShow) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);
      if (currentPage > 3) pages.push('...');
      for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) {
        pages.push(i);
      }
      if (currentPage < totalPages - 2) pages.push('...');
      pages.push(totalPages);
    }
    return pages;
  };

  const renderPagination = () => {
    return generatePageNumbers().map((page, index) =>
      typeof page === 'number' ? (
        <button
          key={index}
          className={`page-number ${currentPage === page ? 'active' : ''}`}
          onClick={() => handlePageClick(page)}
        >
          {page}
        </button>
      ) : (
        <span key={index} className="pagination-dots">
          {page}
        </span>
      )
    );
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value.toLowerCase());
    setCurrentPage(1);
  };

  const handleItemsPerPageChange = (event) => {
    setRowsPerPage(Number(event.target.value));
    setCurrentPage(1);
  };


  return (
    <div className="enrollment-container">

      <div className="search-create-container">
        <div className="input-container">
          <input
            type="text"
            id="searchInput"
            placeholder=""
            className={`search-input ${searchTerm ? "has-value" : ""}`}
            value={searchTerm}
            onChange={handleSearch}
          />
          <label htmlFor="searchInput" className="floating-label">
            🔍 Search
          </label>
        </div>
        {/* {hasPrivilege(userData, 'ACADEMY_FEATURES', 'FEATURES_LIST_MODIFY') && ( */}
        <button
          className="create-button-new"
          onClick={handleCreate}
        >
          <span className="create-button__icon">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              width="23"
              height="23"
              className="create-button__svg"
            >
              <path
                d="M12 5v14M5 12h14"
                stroke="white"
                strokeWidth="2.3"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
          <span className="create-button__label">
            Add Features
          </span>
        </button>
        {/* )} */}
      </div>

      <div className="items-and-dues">
        <select className="items-per-page-select" value={rowsPerPage} onChange={handleItemsPerPageChange}>
          <option value={10}>10 items per page</option>
          <option value={50}>50 items per page</option>
          <option value={75}>75 items per page</option>
          <option value={100}>100 items per page</option>
        </select>
        <div className="pagination-selector">
          <div className="button-group">
            <div className="group-label">Items per page:</div>
            <div className="button-options">
              {[10, 50, 75, 100].map((option) => (
                <button
                  key={option}
                  onClick={() =>
                    handleItemsPerPageChange({ target: { value: option } })
                  }
                  className={`page-button ${rowsPerPage === option ? 'active' : ''}`}
                >
                  <span className="button-text">{option}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        <button className="tt-action-btn" onClick={fetchFeatures}>
          <FiRefreshCw className={`refresh-icon ${loading ? 'spin' : ''}`} />
          Refresh
        </button>
      </div>

      {!loading && !error && filteredFeatures.length === 0 && (
        <div className="error-message">No data available</div>
      )}

      <div className="tt-table-container">
        {loading ? (
          <div className="tt-loading">
            <div className="tt-loading-spinner"></div>
            <p>Loading Academy Features...</p>
          </div>
        ) : (
          <>
            <table className="tt-table">
              <thead>
                <tr>
                  {header.map(({ key, label }) => (
                    <th key={key} onClick={() => handleSort(key)}>
                      <div className="th-content">
                        <span>{label}</span>
                        <FiChevronDown
                          className={`sort-icon ${sortConfig.key === key ? 'active' : ''} ${sortConfig.direction === 'desc' ? 'desc' : ''}`}
                        />
                      </div>
                    </th>
                  ))}
                  {/* {hasPrivilege(userData, 'ACADEMY_FEATURES', 'FEATURES_LIST_MODIFY') && ( */}
                  <th>Action</th>
                  {/* )} */}
                </tr>
              </thead>
              <tbody>
                {currentRows.map(f => (
                  <tr key={f.id}>
                    <td>{f.name}</td>
                    <td>{f.type}</td>
                    <td>{f.amenities}</td>
                    {/* {hasPrivilege(userData, 'ACADEMY_FEATURES', 'FEATURES_LIST_MODIFY') && ( */}
                    <td className="action-buttons">
                      <button
                        className="edit-btn tooltip-btn"
                        onClick={(e) => handleEdit(e, f.id)}
                        title="Edit"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="18"
                          height="18"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="white"
                          strokeWidth="2"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M16.862 3.487a2.12 2.12 0 0 1 3.001 3.001L7.5 18.85l-4 1 1-4L16.862 3.487z"
                          />
                        </svg>
                        <span className="tooltip-text">Edit</span>
                      </button>

                    </td>
                    {/* )} */}
                  </tr>
                ))}
              </tbody>
            </table>
            {filteredFeatures.length === 0 && (
              <div className="tt-empty-state">
                <p>No features found.</p>
              </div>
            )}
          </>
        )}
      </div>

      <div className="pagination">
        <div className="showing-entries">
          Showing {indexOfFirstEntry} to {indexOfLastEntry} of {totalEntries} entries
        </div>
        <button
          className="prev-button"
          onClick={handlePrevPage}
          disabled={currentPage === 1}
        >
          Previous
        </button>
        {renderPagination()}
        <button
          className="next-button"
          onClick={handleNextPage}
          disabled={currentPage === totalPages}
        >
          Next
        </button>
      </div>

    </div>
  );
};


export default AcademyFeatures
